import { Component } from '@angular/core';
import { environment } from '../environments/environment';
import { Router, RouterOutlet, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './services/auth.service';
import { NotificationService } from './services/notification.service';
import { formatApiError } from './services/error.util';
import { CommonModule } from '@angular/common';
import { ToastComponent } from './components/toast/toast.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, ToastComponent, ConfirmDialogComponent],
  template: `
    <nav class="navbar" [style.background]="navBg">
      <div class="nav-brand">My Eternal Spring</div>
      <div class="nav-links">
        <div class="nav-dropdown" (mouseenter)="openMenu='backup'" (mouseleave)="openMenu=''">
          <span class="nav-dropdown-btn">Backup</span>
          <div class="nav-dropdown-content" *ngIf="openMenu==='backup'">
            <a (click)="exportSql(); openMenu=''">Export as SQL</a>
          </div>
        </div>
        <div class="nav-dropdown" (mouseenter)="openMenu='items'" (mouseleave)="openMenu=''">
          <span class="nav-dropdown-btn">Items</span>
          <div class="nav-dropdown-content" *ngIf="openMenu==='items'">
            <a routerLink="/oils" (click)="openMenu=''">Essential Oils</a>
            <a routerLink="/carrier-oils" (click)="openMenu=''">Carrier Oils</a>
            <a routerLink="/butters" (click)="openMenu=''">Butters</a>
            <a routerLink="/waxes" (click)="openMenu=''">Waxes</a>
            <a routerLink="/antioxidants" (click)="openMenu=''">Antioxidants</a>
            <div class="dropdown-sep"></div>
            <a routerLink="/definitions" (click)="openMenu=''">Definitions</a>
          </div>
        </div>
        <div class="nav-dropdown" (mouseenter)="openMenu='balms'" (mouseleave)="openMenu=''">
          <span class="nav-dropdown-btn">Balms</span>
          <div class="nav-dropdown-content" *ngIf="openMenu==='balms'">
            <a routerLink="/balms" (click)="openMenu=''">Balms</a>
            <a routerLink="/balm-defaults" (click)="openMenu=''">Balm Defaults</a>
          </div>
        </div>
        <div class="nav-dropdown" (mouseenter)="openMenu='reference'" (mouseleave)="openMenu=''">
          <span class="nav-dropdown-btn">Reference</span>
          <div class="nav-dropdown-content" *ngIf="openMenu==='reference'">
            <a routerLink="/starter-kits" (click)="openMenu=''">Starter Kits</a>
            <a routerLink="/suppliers" (click)="openMenu=''">Suppliers</a>
            <a routerLink="/references" (click)="openMenu=''">References</a>
          </div>
        </div>
        <a routerLink="/blending" class="nav-standalone">Blending</a>
      </div>
      <div class="nav-auth">
        <button *ngIf="auth.isAuthenticated" (click)="logout()" class="btn-logout">Logout</button>
        <a *ngIf="!auth.isAuthenticated" routerLink="/login" class="btn-login">Login</a>
      </div>
    </nav>
    <main class="container">
      <router-outlet />
    </main>
    <app-toast />
    <app-confirm-dialog />
  `,
  styles: [`
    .navbar { display: flex; align-items: center; color: white; padding: 0 20px; height: 56px; gap: 16px; }
    .nav-brand { font-size: 18px; font-weight: bold; white-space: nowrap; }
    .nav-links { display: flex; gap: 4px; flex: 1; align-items: center; }
    .nav-dropdown { position: relative; }
    .nav-dropdown-btn { color: rgba(255,255,255,0.9); padding: 8px 14px; border-radius: 4px; font-size: 13px; cursor: pointer; user-select: none; }
    .nav-dropdown-btn:hover { background: rgba(255,255,255,0.15); }
    .nav-dropdown-content { position: absolute; top: 100%; left: 0; background: white; border-radius: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.15); min-width: 170px; z-index: 1000; padding: 4px 0; }
    .nav-dropdown-content a { display: block; padding: 8px 16px; color: #333; text-decoration: none; font-size: 13px; cursor: pointer; }
    .nav-dropdown-content a:hover { background: #f0f0e8; color: #2F5496; }
    .dropdown-sep { height: 1px; background: #e0e0e0; margin: 4px 8px; }
    .nav-standalone { color: rgba(255,255,255,0.9); text-decoration: none; padding: 8px 14px; border-radius: 4px; font-size: 13px; }
    .nav-standalone:hover { background: rgba(255,255,255,0.15); }
    .nav-auth { margin-left: auto; display: flex; gap: 8px; }
    .btn-logout, .btn-export { background: rgba(255,255,255,0.15); color: white; border: 1px solid rgba(255,255,255,0.3); padding: 6px 14px; border-radius: 4px; cursor: pointer; font-size: 12px; }
    .btn-logout:hover, .btn-export:hover { background: rgba(255,255,255,0.25); }
    .btn-login { color: white; text-decoration: none; padding: 6px 14px; border: 1px solid rgba(255,255,255,0.3); border-radius: 4px; font-size: 12px; }
    .container { max-width: 1400px; margin: 0 auto; padding: 20px; }
  `]
})
export class AppComponent {
  navBg = environment.envName === 'test' ? '#2E7D32' : '#2F5496';
  openMenu = '';
  constructor(public auth: AuthService, private http: HttpClient, private router: Router, private notify: NotificationService) {}

  logout() {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  exportSql() {
    this.http.get('/api/export/sql', { responseType: 'text' }).subscribe({
      next: (sql) => {
        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        const ts = now.getFullYear().toString() + pad(now.getMonth() + 1) + pad(now.getDate()) + '_' + pad(now.getHours()) + pad(now.getMinutes()) + pad(now.getSeconds());
        const filename = 'my_eternal_spring_data_web_' + ts + '.sql';
        const blob = new Blob([sql], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.notify.success('SQL export downloaded successfully');
      },
      error: (err) => {
        this.notify.error('SQL export failed:\n' + formatApiError(err));
      }
    });
  }
}
